import type { FC } from 'react';
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import Animated from 'react-native-reanimated';
import { SvgIconType } from '@thermenresorts/icons';
import type ApiContent from 'src/core/models/api/ApiContent';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import { useExerciseStore } from 'src/domain/stores/useExerciseStore';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import { exerciseOpensInDialog } from 'src/core/utils/Exercise';
import { gridColumnWidth } from 'src/presentation/helpers/CardGrid';
import { exerciseMetaLine } from 'src/presentation/helpers/ExerciseMeta';
import { useGridCardEntrance } from 'src/presentation/hooks/useGridCardEntrance';
import { usePressFeedback } from 'src/presentation/hooks/usePressFeedback';

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

interface Props {
	content: ApiContent;
	index: number;
}

const ExerciseGridCard: FC<Props> = ({ content, index }: Props) => {
	const { width } = useWindowDimensions();
	const { open } = useExerciseStore();

	const meta = exerciseMetaLine(content);
	const entrance = useGridCardEntrance(index);
	const feedback = usePressFeedback();

	return (
		<AnimatedPressable
			accessibilityRole="button"
			style={[styles.card, { width: gridColumnWidth(width) }, entrance]}
			onPress={() => open(content)}
			onPressIn={feedback.onPressIn}
			onPressOut={feedback.onPressOut}
		>
			<Animated.View style={[styles.content, feedback.style]}>
				<View style={styles.thumbnail}>
					<ThermenImage
						contentFit="cover"
						source={content.media ? { uri: content.media } : null}
						style={styles.thumbnailImage}
					/>
					{exerciseOpensInDialog(content) ? (
						<View style={styles.play}>
							<SvgIcon name={SvgIconType.play} size={Dimens.iconSize8} tintColor={Colors.green700} />
						</View>
					) : null}
				</View>
				<View style={styles.text}>
					<Text style={styles.title}>{content.title}</Text>
					{meta === null ? null : <Text style={styles.meta}>{meta}</Text>}
				</View>
			</Animated.View>
		</AnimatedPressable>
	);
};

const styles = StyleSheet.create({
	card: {
		borderRadius: Dimens.borderRadius4,
		backgroundColor: Colors.white,
		overflow: 'hidden',
	},
	content: {
		flex: Dimens.flex1,
	},
	thumbnail: {
		width: '100%',
		aspectRatio: Dimens.aspectRatio2By1,
		backgroundColor: Colors.gray300,
	},
	thumbnailImage: {
		width: '100%',
		height: '100%',
	},
	play: {
		position: 'absolute',
		left: Dimens.padding12,
		bottom: Dimens.padding12,
		width: Dimens.size24,
		height: Dimens.size24,
		borderRadius: Dimens.borderRadiusPill,
		backgroundColor: Colors.white,
		alignItems: 'center',
		justifyContent: 'center',
		paddingLeft: Dimens.padding2,
	},
	text: {
		flex: Dimens.flex1,
		justifyContent: 'space-between',
		gap: Dimens.gap4,
		padding: Dimens.padding12,
	},
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight18,
		color: Colors.gray900,
	},
	meta: {
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize12,
		lineHeight: Dimens.lineHeight21,
		color: Colors.gray650,
	},
});

export default ExerciseGridCard;
